import { decodeHTMLAttribute } from 'entities'

/**
 * html-structure.ts
 *
 * Where a page's HTML comments, scripts and styles are, decided the way the browser's HTML tokenizer
 * decides it (WHATWG HTML §13.2.5). This is the first of the browser's two layers: it cuts the page
 * into pieces knowing nothing of JavaScript or CSS. Only afterwards, and only inside a range found
 * here, does anything apply JavaScript or CSS rules. A comment or string can therefore never move
 * where a script ends — the same order the browser keeps.
 *
 * The analyser and the browser must agree on these boundaries: where they differ, a page can show
 * the scan one program and run another.
 */

export type HtmlRange =
  /** `<!-- … -->`, and the bogus comments `<?…>`, `<!…>` and `</ …>` that the browser also treats as comments. */
  | { kind: 'comment'; start: number; end: number }
  /**
   * The content between `<script …>` and its end tag. `executable` is false for data blocks such as
   * type="application/json"; `module` is true for type="module", where `<!--` and `-->` are not comments.
   */
  | { kind: 'script'; start: number; end: number; executable: boolean; module: boolean }
  /** The content between `<style …>` and `</style>`. */
  | { kind: 'style'; start: number; end: number }
  /** The value of an event-handler attribute — `onclick="…"` — which the browser runs as JavaScript. `event` is e.g. "click". */
  | { kind: 'handler'; event: string; start: number; end: number }

/** Elements whose content is text up to their own end tag: a `<script>` inside one is not a tag. `style` and `script` are handled apart. */
const TEXT_ONLY_ELEMENTS = new Set(['textarea', 'title', 'xmp', 'iframe', 'noembed', 'noframes', 'noscript'])

/** The type values that make a script run (HTML §4.12.1.1); anything else is a data block. */
const EXECUTABLE_SCRIPT_TYPE =
  /^(?:module|(?:text|application)\/(?:x-)?(?:java|ecma)script|text\/(?:jscript|livescript|javascript1\.[0-5]))$/i

export function htmlStructure(source: string): HtmlRange[] {
  const ranges: HtmlRange[] = []
  let index = 0

  while (index < source.length) {
    const lt = source.indexOf('<', index)
    if (lt < 0) break

    if (source.startsWith('<!--', lt)) {
      const end = commentEnd(source, lt)
      ranges.push({ kind: 'comment', start: lt, end })
      index = end
      continue
    }

    const next = source[lt + 1] ?? ''
    if (next === '!' || next === '?' || (next === '/' && !/[A-Za-z]/.test(source[lt + 2] ?? ''))) {
      /* Bogus comment: runs to the first `>`. A `</` followed by a non-letter is one too. A doctype ends the same way but is not a comment. */
      const close = source.indexOf('>', lt)
      const end = close < 0 ? source.length : close + 1
      if (!/^<!doctype/i.test(source.slice(lt, lt + 9))) ranges.push({ kind: 'comment', start: lt, end })
      index = end
      continue
    }

    if (next === '/') {
      index = readTag(source, lt + 2).end
      continue
    }

    if (!/[A-Za-z]/.test(next)) {
      index = lt + 1
      continue
    }

    const tag = readTag(source, lt + 1)
    for (const handler of tag.handlers) ranges.push({ kind: 'handler', ...handler })
    if (tag.name === 'plaintext') break

    if (tag.name === 'script') {
      const end = scriptDataEnd(source, tag.end)
      const type = (tag.attributes.get('type') ?? '').trim()
      ranges.push({
        kind: 'script',
        start: tag.end,
        end,
        executable: type === '' || EXECUTABLE_SCRIPT_TYPE.test(type),
        module: type.toLowerCase() === 'module',
      })
      index = endTagEnd(source, end)
      continue
    }

    if (tag.name === 'style' || TEXT_ONLY_ELEMENTS.has(tag.name)) {
      const end = textEnd(source, tag.end, tag.name)
      if (tag.name === 'style') ranges.push({ kind: 'style', start: tag.end, end })
      index = endTagEnd(source, end)
      continue
    }

    index = tag.end
  }

  return ranges
}

/**
 * One start tag read from just after its `<`, as the browser reads it: the attributes, decoded, and the
 * index just past its `>`. A `>` inside a quoted value does not end the tag. `closed` is false when the
 * source ends first.
 */
export function readStartTag(source: string, nameStart: number): { attributes: Map<string, string>; end: number; closed: boolean } {
  const tag = readTag(source, nameStart)
  return { attributes: tag.attributes, end: tag.end, closed: source[tag.end - 1] === '>' }
}

/**
 * End of an HTML comment opened at `start`: just past `-->` or `--!>`. `<!-->` and `<!--->` close at
 * once — the spec's abrupt closing — and an unclosed comment runs to the end.
 */
function commentEnd(source: string, start: number): number {
  if (source.startsWith('<!-->', start)) return start + 5
  if (source.startsWith('<!--->', start)) return start + 6
  const candidates = [source.indexOf('-->', start + 4), source.indexOf('--!>', start + 4)].filter((at) => at >= 0)
  if (!candidates.length) return source.length
  const at = Math.min(...candidates)
  return at + (source.startsWith('--!>', at) ? 4 : 3)
}

type Tag = {
  name: string
  attributes: Map<string, string>
  /** Offsets of each event-handler attribute value, so its JavaScript can be scanned in place. */
  handlers: { event: string; start: number; end: number }[]
  end: number
}

/**
 * Read one tag from just after its `<` (or `</`): the name, the attributes and the index past its `>`.
 * Attribute values are read whole, quoted or not, so whatever a value contains — `<script`, `<!--`,
 * `>` inside quotes — is part of the value and never markup. Handler offsets point at the raw value, which
 * is scanned in place; the decoded value is what `attributes` holds.
 */
function readTag(source: string, from: number): Tag {
  let index = from
  const nameStart = index
  while (index < source.length && !/[\s/>]/.test(source[index])) index++
  const name = source.slice(nameStart, index).toLowerCase()
  const attributes = new Map<string, string>()
  const handlers: Tag['handlers'] = []

  while (index < source.length) {
    const char = source[index]
    if (char === '>') return { name, attributes, handlers, end: index + 1 }
    if (/[\s/]/.test(char)) {
      index++
      continue
    }

    const attrStart = index
    /* The first character of an attribute name may be `=`; after that `=` separates name from value. */
    index++
    while (index < source.length && !/[\s/>=]/.test(source[index])) index++
    const attrName = source.slice(attrStart, index).toLowerCase()

    let after = index
    while (after < source.length && /\s/.test(source[after])) after++
    if (source[after] !== '=') {
      if (!attributes.has(attrName)) attributes.set(attrName, '')
      continue
    }

    index = after + 1
    while (index < source.length && /\s/.test(source[index])) index++
    const quote = source[index]
    let valueStart: number
    let valueEnd: number
    if (quote === '"' || quote === "'") {
      const close = source.indexOf(quote, index + 1)
      valueStart = index + 1
      valueEnd = close < 0 ? source.length : close
      index = close < 0 ? source.length : close + 1
    } else {
      valueStart = index
      while (index < source.length && !/[\s>]/.test(source[index])) index++
      valueEnd = index
    }
    /* The first occurrence of a repeated attribute wins, as in the browser. */
    if (attributes.has(attrName)) continue
    /* Decoded as the browser does before using the value: https&#58;// is https:// to everything that reads it. */
    attributes.set(attrName, decodeHTMLAttribute(source.slice(valueStart, valueEnd)))
    if (/^on[a-z]+$/.test(attrName)) handlers.push({ event: attrName.slice(2), start: valueStart, end: valueEnd })
  }

  return { name, attributes, handlers, end: source.length }
}

/** Whether an end tag for `name` starts at `index`: `</name` followed by whitespace, `/` or `>`, any case. */
function isEndTag(source: string, index: number, name: string): boolean {
  if (source[index] !== '<' || source[index + 1] !== '/') return false
  if (source.slice(index + 2, index + 2 + name.length).toLowerCase() !== name) return false
  return /[\s/>]/.test(source[index + 2 + name.length] ?? '')
}

/** Content end of a text-only element: its own end tag, nothing else. */
function textEnd(source: string, from: number, name: string): number {
  for (let index = source.indexOf('<', from); index >= 0; index = source.indexOf('<', index + 1)) {
    if (isEndTag(source, index, name)) return index
  }
  return source.length
}

/** Index past the end tag starting at `index`, or the end of the source when there is none. */
function endTagEnd(source: string, index: number): number {
  if (index >= source.length) return source.length
  return readTag(source, index + 2).end
}

/**
 * Content end of a script, by the spec's script data states. A `</script` ends it — except while
 * double-escaped: after `<!--` then `<script`, the next `</script` only returns to the escaped state,
 * and the script runs on. `-->` leaves the escaped states. JavaScript plays no part in any of this.
 */
function scriptDataEnd(source: string, from: number): number {
  let state: 'data' | 'escaped' | 'double-escaped' = 'data'
  let dashes = 0

  for (let index = from; index < source.length; index++) {
    const char = source[index]

    if (state === 'data') {
      if (source.startsWith('<!--', index)) {
        state = 'escaped'
        dashes = 2
        index += 3
        continue
      }
      if (isEndTag(source, index, 'script')) return index
      continue
    }

    if (char === '-') {
      dashes++
      continue
    }
    if (char === '>' && dashes >= 2) {
      state = 'data'
      dashes = 0
      continue
    }
    dashes = 0

    if (state === 'escaped') {
      if (isEndTag(source, index, 'script')) return index
      if (source.slice(index, index + 7).toLowerCase() === '<script' && /[\s/>]/.test(source[index + 7] ?? '')) {
        state = 'double-escaped'
        index += 6
      }
      continue
    }

    if (isEndTag(source, index, 'script')) {
      state = 'escaped'
      index += 7
    }
  }

  return source.length
}
